import { useState, useEffect, useRef, useCallback } from 'react'
import { useAuth } from '../context/AuthContext'
import { FortisPayment, type FortisPaymentHandle } from '../components/payments/FortisPayment'
import * as bandsApi from '../api/bands'
import { PaymentPending } from '../components/payments/PaymentPending'
import { PAYMENT_PENDING_TEXT, isDefiniteRefusal, pendingRefusal, serverError } from '../api/paymentOutcome'
import type { BandOrderItem, BandCost, BandSettings, PreviousBandOrder, FortisTransactionData } from '../types'
import { BAND_LETTERS_MAX, bandRowError, bandLettersLength, isEmptyBandRow, bandRowKey, capBandLetters, orderableBandRows } from './bandOrderRules'

const BAND_COLORS = ['Green', 'White', 'Blue', 'Yellow', 'Red', 'Violet', 'Gold', 'Pink'] as const

/** What a row's duplicate check decided, and why when it refused. */
interface BandCheckVerdict {
  ok: boolean
  message: string | null
}

interface BandRow extends BandOrderItem {
  duplicateError: string | null
  checking: boolean
  /**
   * The letters|from|to the server last confirmed available for this row.
   *
   * Lauren Gandara, 2026-09-17: she entered lettering used last year, was told
   * so, "backspaced, tried something else, then entered the original TPF
   * lettering again and it let me move on". The verdict used to be a single
   * error string that a later edit never invalidated, so a stale pass from the
   * intermediate lettering carried the original one through Calculate Cost.
   * Keeping the values the verdict belongs to means an edit retires it.
   */
  verifiedKey: string | null
}

function emptyRow(defaultColor: string): BandRow {
  return {
    bandLetters: '',
    characterCode: '',
    bandFrom: 0,
    bandTo: 0,
    bandColor: defaultColor,
    duplicateError: null,
    checking: false,
    verifiedKey: null,
  }
}

export function Bands() {
  const { user } = useAuth()
  const fortisRef = useRef<FortisPaymentHandle>(null)

  const [settings, setSettings] = useState<BandSettings | null>(null)
  const [previousOrders, setPreviousOrders] = useState<PreviousBandOrder[]>([])
  const [rows, setRows] = useState<BandRow[]>([])
  // The latest rows, for the async flows: a blur check and Calculate Cost can
  // run in the same tick, and a closure over a stale rows array is what let a
  // refused lettering through (Lauren, 2026-09-17).
  const rowsRef = useRef<BandRow[]>([])
  rowsRef.current = rows
  const [costs, setCosts] = useState<{ items: BandCost[]; total: number } | null>(null)
  // The rows the priced total belongs to, so the cost table labels the right
  // lettering even when an empty row sits between two filled ones.
  const [pricedRows, setPricedRows] = useState<BandRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [step, setStep] = useState<'form' | 'shipping' | 'payment' | 'pending' | 'success'>('form')
  const [pendingMessage, setPendingMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [paymentData, setPaymentData] = useState<{ clientToken: string; env: 'sandbox' | 'production'; transactionId: string } | null>(null)
  const [shipping, setShipping] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    address: '',
    city: '',
    state: '',
    zip: '',
    phone: user?.phone || '',
  })

  // Load settings and previous orders
  useEffect(() => {
    async function init() {
      try {
        const [settingsData, prevOrders] = await Promise.all([
          bandsApi.getBandSettings(),
          bandsApi.getPreviousOrders(),
        ])
        setSettings(settingsData)
        setPreviousOrders(prevOrders)
        setRows([emptyRow(settingsData.defaultColor)])
        if (settingsData.pendingCapture) {
          setPendingMessage(settingsData.pendingMessage || PAYMENT_PENDING_TEXT)
          setStep('pending')
        }
      } catch {
        setError('Failed to load band ordering settings. Please try again.')
      } finally {
        setLoading(false)
      }
    }
    init()
  }, [])

  // Update shipping when user loads
  useEffect(() => {
    if (user) {
      setShipping((s) => ({
        ...s,
        firstName: s.firstName || user.firstName || '',
        lastName: s.lastName || user.lastName || '',
        phone: s.phone || user.phone || '',
      }))
    }
  }, [user])

  const updateRow = useCallback((index: number, field: keyof BandRow, value: string | number) => {
    setRows((prev) => {
      const updated = [...prev]
      const row = { ...updated[index] }
      ;(row as Record<string, unknown>)[field] = value

      // Auto-extract character code from bandLetters
      if (field === 'bandLetters') {
        row.characterCode = (value as string).replace(/[^a-zA-Z]/g, '').substring(0, 4).toUpperCase()
      }

      // Any edit to what was checked retires the verdict, so the row has to be
      // checked again before the order can be priced.
      if (field === 'bandLetters' || field === 'bandFrom' || field === 'bandTo') {
        row.duplicateError = null
        row.verifiedKey = null
      }

      updated[index] = row
      return updated
    })
    // Clear costs when form changes
    setCosts(null)
  }, [])

  const addRow = useCallback(() => {
    if (!settings) return
    setRows((prev) => [...prev, emptyRow(settings.defaultColor)])
    setCosts(null)
  }, [settings])

  const removeRow = useCallback((index: number) => {
    setRows((prev) => {
      if (prev.length <= 1) return prev
      return prev.filter((_, i) => i !== index)
    })
    setCosts(null)
  }, [])

  // Check one row's letters and range against the server. Returns true when the
  // row is available, and records WHICH values were checked so a later edit
  // cannot inherit the verdict.
  const checkDuplicate = useCallback(async (index: number): Promise<BandCheckVerdict> => {
    const current = rowsRef.current
    const row = current[index]
    if (!row || isEmptyBandRow(row)) return { ok: true, message: null }
    // A row the form itself can refuse (no numbers, lettering too long) is not
    // sent to the server; handleCalculate reports it.
    const ownError = bandRowError(row)
    if (ownError !== null) return { ok: false, message: ownError }

    const key = bandRowKey(row)
    setRows((prev) => {
      const updated = [...prev]
      updated[index] = { ...updated[index], checking: true, duplicateError: null }
      return updated
    })

    try {
      const personName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim()
      const existingItems = current
        .filter((_, i) => i !== index)
        .map((r) => ({ bandLetters: r.bandLetters, bandFrom: r.bandFrom, bandTo: r.bandTo, bandColor: r.bandColor, characterCode: r.characterCode }))

      const result = await bandsApi.checkDuplicate(
        row.bandLetters,
        row.bandFrom,
        row.bandTo,
        existingItems,
        personName,
      )

      setRows((prev) => {
        const updated = [...prev]
        // The row may have been edited while the request was in flight. The
        // verdict belongs to the values that were sent, so it is only applied
        // when the row still holds them.
        if (bandRowKey(updated[index]) !== key) {
          updated[index] = { ...updated[index], checking: false }
          return updated
        }
        updated[index] = {
          ...updated[index],
          checking: false,
          duplicateError: result.valid ? null : result.message,
          verifiedKey: result.valid ? key : null,
        }
        return updated
      })
      return { ok: result.valid, message: result.valid ? null : result.message }
    } catch {
      setRows((prev) => {
        const updated = [...prev]
        updated[index] = { ...updated[index], checking: false, duplicateError: 'Error checking availability', verifiedKey: null }
        return updated
      })
      return { ok: false, message: 'Error checking availability' }
    }
  }, [user])

  // Calculate costs
  const handleCalculate = useCallback(async () => {
    const filled = rowsRef.current
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => !isEmptyBandRow(row))

    if (filled.length === 0) {
      setError('Please add at least one band order')
      return
    }

    // A row with letters and no band number is an error, never a row that is
    // quietly left out of the order (Lauren, 2026-09-17).
    const rowErrors = filled
      .map(({ row, index }) => ({ index, message: bandRowError(row) }))
      .filter((e): e is { index: number; message: string } => e.message !== null)
    if (rowErrors.length > 0) {
      setRows((prev) => {
        const updated = [...prev]
        for (const { index, message } of rowErrors) {
          updated[index] = { ...updated[index], duplicateError: message }
        }
        return updated
      })
      setError(rowErrors[0].message)
      return
    }

    setError('')
    setSubmitting(true)
    try {
      // Every row whose current letters and range have not been confirmed by
      // the server is checked now, in order, before anything is priced. This is
      // the gate a stale verdict used to walk past. The verdicts are read from
      // the calls themselves rather than from state, which has not necessarily
      // re-rendered by the time the await returns.
      for (const { row, index } of filled) {
        if (row.verifiedKey === bandRowKey(row)) continue
        const verdict = await checkDuplicate(index)
        if (!verdict.ok) {
          setError(verdict.message || 'These band letters are not available.')
          setCosts(null)
          return
        }
      }

      const ordered = filled.map(({ row }) => row)
      const items = ordered.map((r) => ({
        bandLetters: r.bandLetters,
        characterCode: r.characterCode,
        bandFrom: r.bandFrom,
        bandTo: r.bandTo,
        bandColor: r.bandColor,
      }))
      const result = await bandsApi.calculateCosts(items)
      setPricedRows(ordered)
      setCosts(result)
    } catch (err) {
      // The server runs the same rules, so show what it said rather than a
      // generic failure.
      const serverMessage = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
      setError(serverMessage || 'Failed to calculate costs. Please try again.')
      setCosts(null)
    } finally {
      setSubmitting(false)
    }
  }, [checkDuplicate])

  // Validate and proceed to shipping
  const handleContinueToShipping = useCallback(() => {
    if (!costs) return
    setStep('shipping')
  }, [costs])

  // Start payment
  const handleStartPayment = useCallback(async () => {
    setError('')
    setSubmitting(true)

    const requiredFields = ['firstName', 'lastName', 'address', 'city', 'state', 'zip', 'phone'] as const
    const missing = requiredFields.filter((f) => !shipping[f])
    if (missing.length > 0) {
      setError('Please fill in all shipping fields')
      setSubmitting(false)
      return
    }

    try {
      const items = orderableBandRows(rowsRef.current)
        .map((r) => ({
          bandLetters: r.bandLetters,
          characterCode: r.characterCode,
          bandFrom: r.bandFrom,
          bandTo: r.bandTo,
          bandColor: r.bandColor,
        }))

      const result = await bandsApi.startBandOrder(items, shipping)
      setPaymentData({
        clientToken: result.clientToken,
        env: result.env,
        transactionId: result.transactionId,
      })
      setStep('payment')
    } catch (err) {
      // An earlier band order still waits on its capture: no payment form.
      const held = pendingRefusal(err)
      if (held) {
        setPendingMessage(held)
        setStep('pending')
        return
      }
      // /bands/order/start re-runs the row rules and the used-letters check, so
      // a refusal here carries the reason.
      const serverMessage = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
      if (serverMessage) {
        setError(serverMessage)
        setStep('form')
      } else {
        setError('Failed to start payment. Please try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }, [shipping])

  // Complete each order once from this page. A second 'done' while the first
  // completion is still capturing gets roster's lock-busy answer, and treating
  // that as a refusal would drop the order and offer a new one mid-capture.
  const completedRef = useRef(false)

  // Handle successful Fortis payment
  const handlePaymentSuccess = useCallback(async (fortisData: FortisTransactionData) => {
    if (!paymentData) return
    if (completedRef.current) return
    completedRef.current = true
    setSubmitting(true)
    setError('')

    try {
      const result = await bandsApi.completeBandOrder(paymentData.transactionId, {
        id: fortisData.id,
        status_code: fortisData.status_code,
      })
      if (result?.pending) {
        // The payment form is gone and stays gone: the member must not pay again.
        setPendingMessage(result.message || PAYMENT_PENDING_TEXT)
        setPaymentData(null)
        setStep('pending')
        return
      }
      setStep('success')
    } catch (err) {
      completedRef.current = false
      if (isDefiniteRefusal(err)) {
        // The server refused this order for good: Proceed to Payment starts a new one.
        setError(serverError(err) ?? 'There was an error completing your order. Please contact support.')
        setPaymentData(null)
        setStep('shipping')
        return
      }
      setError('Payment was processed but there was an error completing your order. Please contact support.')
    } finally {
      setSubmitting(false)
    }
  }, [paymentData])

  const handlePaymentError = useCallback((msg: string) => {
    setError(msg)
  }, [])

  if (step === 'pending') {
    return <PaymentPending message={pendingMessage} />
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" />
        <span className="ml-3 text-text-muted">Loading band ordering...</span>
      </div>
    )
  }

  if (!settings) {
    return (
      <div className="text-center py-16">
        <p className="text-error">Unable to load band ordering settings.</p>
      </div>
    )
  }

  if (!settings.bandOrderEnabled) {
    return (
      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl font-heading font-bold text-text mb-4">Order Personalized Bands</h1>
        <div className="bg-warning/10 border border-warning/30 text-text rounded-md px-4 py-3">
          Band ordering is currently disabled. Please check back later.
        </div>
      </div>
    )
  }

  if (!settings.orderWindowOpen) {
    return (
      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl font-heading font-bold text-text mb-4">Order Personalized Bands</h1>
        <div className="bg-warning/10 border border-warning/30 text-text rounded-md px-4 py-3">
          Personalized band ordering is closed for the season.
          {settings.reopenDate
            ? ` Ordering reopens ${settings.reopenDate}.`
            : ' Ordering is available October 1 through March 31.'}
        </div>
      </div>
    )
  }

  // Success state
  if (step === 'success') {
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <div className="text-success text-5xl mb-4">&#10003;</div>
        <h1 className="text-2xl font-heading font-bold text-text mb-2">Band Order Placed!</h1>
        <p className="text-text-muted mb-6">
          Your personalized band order for {settings.year} has been submitted successfully.
        </p>
        <a href="/dashboard" className="inline-block px-6 py-2.5 bg-primary text-text-inverse rounded-md hover:bg-primary-light transition-colors">
          Back to Dashboard
        </a>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-heading font-bold text-text mb-2">
        Order Personalized Bands
      </h1>
      <p className="text-text-muted mb-6">
        {settings.year} Season &mdash; Default color: {settings.defaultColor}
      </p>

      {settings.testerPreview && (
        <div className="bg-warning/10 border border-warning/30 text-text rounded-md px-4 py-3 mb-4 text-sm">
          Test access: personalized band ordering is open for your account only while the office
          tests it. Any order you place here is a real order and will be charged.
        </div>
      )}

      {error && (
        <div className="bg-error/10 border border-error/30 text-error rounded-md px-4 py-3 mb-4 text-sm">
          {error}
        </div>
      )}

      {/* Step: Form */}
      {step === 'form' && (
        <>
          <div className="space-y-4 mb-6">
            {rows.map((row, index) => (
              <BandRowForm
                key={index}
                index={index}
                row={row}
                defaultColor={settings.defaultColor}
                onUpdate={updateRow}
                onRemove={removeRow}
                onBlurCheck={checkDuplicate}
                canRemove={rows.length > 1}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={addRow}
            className="text-sm text-primary hover:underline mb-6"
          >
            + Add another band order
          </button>

          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={handleCalculate}
              disabled={submitting}
              className="px-6 py-2.5 bg-primary text-text-inverse rounded-md hover:bg-primary-light transition-colors disabled:opacity-50"
            >
              {submitting ? 'Calculating...' : 'Calculate Cost'}
            </button>
          </div>

          {/* Cost preview */}
          {costs && (
            <div className="mt-6 border border-border rounded-lg p-4 bg-surface">
              <h3 className="font-heading font-semibold text-text mb-3">Cost Summary</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-border">
                    <th className="pb-2 text-text-muted">Item</th>
                    <th className="pb-2 text-text-muted text-right">Bands</th>
                    <th className="pb-2 text-text-muted text-right">Cost</th>
                    <th className="pb-2 text-text-muted text-right">Shipping</th>
                    <th className="pb-2 text-text-muted text-right">Color</th>
                    <th className="pb-2 text-text-muted text-right">Fee</th>
                    <th className="pb-2 text-text-muted text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {costs.items.map((item, i) => (
                    <tr key={i} className="border-b border-border-light">
                      <td className="py-2 text-text">{pricedRows[i]?.bandLetters || `Item ${i + 1}`}</td>
                      <td className="py-2 text-text text-right">{item.bandTotal}</td>
                      <td className="py-2 text-text text-right">${item.itemCost.toFixed(2)}</td>
                      <td className="py-2 text-text text-right">${item.shippingCost.toFixed(2)}</td>
                      <td className="py-2 text-text text-right">${item.colorCharge.toFixed(2)}</td>
                      <td className="py-2 text-text text-right">${item.orderFee.toFixed(2)}</td>
                      <td className="py-2 text-text font-medium text-right">${item.total.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={6} className="pt-3 text-right font-semibold text-text">Total:</td>
                    <td className="pt-3 text-right font-bold text-text">${costs.total.toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>

              <button
                type="button"
                onClick={handleContinueToShipping}
                className="mt-4 px-6 py-2.5 bg-primary text-text-inverse rounded-md hover:bg-primary-light transition-colors"
              >
                Continue to Shipping
              </button>
            </div>
          )}

          {/* Previous orders */}
          {previousOrders.length > 0 && (
            <div className="mt-8 border border-border rounded-lg p-4 bg-surface">
              <h3 className="font-heading font-semibold text-text mb-3">Previous Orders</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-border">
                    <th className="pb-2 text-text-muted">Code</th>
                    <th className="pb-2 text-text-muted">Range</th>
                    <th className="pb-2 text-text-muted">Color</th>
                    <th className="pb-2 text-text-muted">Season</th>
                  </tr>
                </thead>
                <tbody>
                  {previousOrders.map((order, i) => (
                    <tr key={i} className="border-b border-border-light">
                      <td className="py-2 text-text">{order.fullCode}</td>
                      <td className="py-2 text-text">{order.startingBand} - {order.endingBand}</td>
                      <td className="py-2 text-text">{order.color || '-'}</td>
                      <td className="py-2 text-text">
                        {order.season}
                        {order.isPreviousYear && <span className="ml-1 text-xs text-success">(recent)</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Step: Shipping */}
      {step === 'shipping' && (
        <div className="border border-border rounded-lg p-6 bg-surface">
          <h2 className="font-heading font-semibold text-text mb-4">Shipping Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <ShippingField label="First Name" value={shipping.firstName} onChange={(v) => setShipping((s) => ({ ...s, firstName: v }))} />
            <ShippingField label="Last Name" value={shipping.lastName} onChange={(v) => setShipping((s) => ({ ...s, lastName: v }))} />
            <div className="sm:col-span-2">
              <ShippingField label="Address" value={shipping.address} onChange={(v) => setShipping((s) => ({ ...s, address: v }))} />
            </div>
            <ShippingField label="City" value={shipping.city} onChange={(v) => setShipping((s) => ({ ...s, city: v }))} />
            <ShippingField label="State" value={shipping.state} onChange={(v) => setShipping((s) => ({ ...s, state: v }))} />
            <ShippingField label="ZIP Code" value={shipping.zip} onChange={(v) => setShipping((s) => ({ ...s, zip: v }))} />
            <ShippingField label="Phone" value={shipping.phone} onChange={(v) => setShipping((s) => ({ ...s, phone: v }))} />
          </div>

          <div className="mt-4 p-3 bg-surface-alt rounded-md text-sm text-text-muted">
            Total: <span className="font-semibold text-text">${costs?.total.toFixed(2)}</span>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={() => setStep('form')}
              className="px-4 py-2 border border-border rounded-md text-text hover:bg-surface-hover transition-colors"
            >
              Back
            </button>
            <button
              type="button"
              onClick={handleStartPayment}
              disabled={submitting}
              className="px-6 py-2.5 bg-primary text-text-inverse rounded-md hover:bg-primary-light transition-colors disabled:opacity-50"
            >
              {submitting ? 'Processing...' : 'Proceed to Payment'}
            </button>
          </div>
        </div>
      )}

      {/* Step: Payment */}
      {step === 'payment' && paymentData && (
        <div className="border border-border rounded-lg p-6 bg-surface">
          <h2 className="font-heading font-semibold text-text mb-4">Payment</h2>
          <p className="text-text-muted text-sm mb-4">
            Total: <span className="font-semibold text-text">${costs?.total.toFixed(2)}</span>
          </p>

          <FortisPayment
            ref={fortisRef}
            clientToken={paymentData.clientToken}
            environment={paymentData.env}
            onSuccess={handlePaymentSuccess}
            onError={handlePaymentError}
            showSubmitButton={true}
          />

          {submitting && (
            <div className="flex items-center mt-4 text-text-muted text-sm">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent mr-2" />
              Completing your order...
            </div>
          )}

          <button
            type="button"
            onClick={() => { setStep('shipping'); setPaymentData(null) }}
            className="mt-4 px-4 py-2 border border-border rounded-md text-text hover:bg-surface-hover transition-colors"
            disabled={submitting}
          >
            Back
          </button>
        </div>
      )}
    </div>
  )
}

function BandRowForm({
  index,
  row,
  defaultColor,
  onUpdate,
  onRemove,
  onBlurCheck,
  canRemove,
}: {
  index: number
  row: BandRow
  defaultColor: string
  onUpdate: (index: number, field: keyof BandRow, value: string | number) => void
  onRemove: (index: number) => void
  onBlurCheck: (index: number) => unknown
  canRemove: boolean
}) {
  return (
    <div className="border border-border rounded-lg p-4 bg-surface">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-text-muted">Band Order #{index + 1}</span>
        {canRemove && (
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="text-sm text-error hover:underline"
          >
            Remove
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <label className="block text-xs font-medium text-text-muted mb-1">Band Letters</label>
          <input
            type="text"
            value={row.bandLetters}
            maxLength={BAND_LETTERS_MAX}
            onChange={(e) => onUpdate(index, 'bandLetters', capBandLetters(e.target.value).toUpperCase())}
            onBlur={() => onBlurCheck(index)}
            placeholder="e.g. SMITH LOFT"
            aria-label={`Band letters for band order ${index + 1}`}
            className="w-full px-3 py-2 border border-border rounded-md bg-surface text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
          />
          <span className="text-xs text-text-muted mt-1 block">
            {bandLettersLength(row.bandLetters)}/{BAND_LETTERS_MAX} characters
            {row.characterCode ? ` \u00b7 Code: ${row.characterCode}` : ''}
          </span>
        </div>

        <div>
          <label className="block text-xs font-medium text-text-muted mb-1">Band From</label>
          <input
            type="number"
            value={row.bandFrom || ''}
            onChange={(e) => onUpdate(index, 'bandFrom', parseInt(e.target.value) || 0)}
            onBlur={() => onBlurCheck(index)}
            placeholder="e.g. 1"
            aria-label={`First band number for band order ${index + 1}`}
            min={1}
            className="w-full px-3 py-2 border border-border rounded-md bg-surface text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-text-muted mb-1">Band To</label>
          <input
            type="number"
            value={row.bandTo || ''}
            onChange={(e) => onUpdate(index, 'bandTo', parseInt(e.target.value) || 0)}
            onBlur={() => onBlurCheck(index)}
            placeholder="e.g. 100"
            aria-label={`Last band number for band order ${index + 1}`}
            min={1}
            className="w-full px-3 py-2 border border-border rounded-md bg-surface text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-text-muted mb-1">Color</label>
          <select
            value={row.bandColor}
            onChange={(e) => onUpdate(index, 'bandColor', e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-md bg-surface text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
          >
            {BAND_COLORS.map((color) => (
              <option key={color} value={color}>
                {color}{color === defaultColor ? ' (default)' : ' (+$30)'}
              </option>
            ))}
          </select>
        </div>
      </div>

      {row.checking && (
        <p className="text-xs text-text-muted mt-2">Checking availability...</p>
      )}
      {row.duplicateError && (
        <p className="text-xs text-error mt-2">{row.duplicateError}</p>
      )}
    </div>
  )
}

function ShippingField({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (value: string) => void
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-text mb-1">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        className="w-full px-3 py-2 border border-border rounded-md bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
      />
    </div>
  )
}
